import React, { useState } from 'react';
import { walletAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

function ArchiveWalletModal({ wallet, availableWallets, onClose, onArchive }) {
  const [step, setStep] = useState(1);
  const [transferData, setTransferData] = useState({
    toWalletId: '',
    description: 'Transfer before wallet archive'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasBalance = wallet.balance > 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const filteredWallets = availableWallets.filter(w =>
    w.ownerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (w.ownerType === 'member' && w.ownerDetails?.ghinNumber?.toString().includes(searchQuery))
  );

  const handleTransferSubmit = async () => {
    if (!transferData.toWalletId) {
      setError('Please select a destination wallet');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await walletAPI.transfer(
        wallet._id,
        transferData.toWalletId,
        wallet.balance,
        transferData.description
      );
      setStep(3);
    } catch (err) {
      console.error('Transfer failed:', err);
      setError(err.response?.data?.message || 'Failed to transfer funds');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      await walletAPI.archiveWallet(wallet._id, 'Wallet archived by user');
      onArchive(transferData);
      onClose();
    } catch (err) {
      console.error('Archive failed:', err);
      setError(err.response?.data?.message || 'Failed to archive wallet');
    } finally {
      setLoading(false);
    }
  };

  const getOwnerTypeIcon = (ownerType) => {
    switch (ownerType) {
      case 'member': return '👤';
      case 'tournament': return '🏌️';
      case 'club': return '🏢';
      case 'contest': return '🏆';
      default: return '💰';
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Archive Wallet</h3>
        <p className="text-slate-600">
          Are you sure you want to archive <strong>{wallet.ownerName}</strong>?
        </p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Current Balance:</span>
            <span className={cn(
              "text-lg font-bold",
              hasBalance ? "text-amber-600" : "text-green-600"
            )}>
              {formatCurrency(wallet.balance)}
            </span>
          </div>
        </CardContent>
      </Card>

      {hasBalance ? (
        <Alert variant="warning" className="border-amber-200 bg-amber-50">
          <div className="flex gap-3">
            <span className="text-2xl">⚠️</span>
            <AlertDescription className="text-amber-800">
              <p className="font-semibold mb-1">This wallet has a balance of {formatCurrency(wallet.balance)}.</p>
              <p>You must transfer all funds out of the wallet before archiving it.</p>
            </AlertDescription>
          </div>
        </Alert>
      ) : (
        <Alert variant="success" className="border-green-200 bg-green-50">
          <div className="flex gap-3">
            <span className="text-2xl">✅</span>
            <AlertDescription className="text-green-800">
              This wallet has a zero balance and is ready to be archived.
            </AlertDescription>
          </div>
        </Alert>
      )}

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        {hasBalance ? (
          <Button onClick={() => setStep(2)} className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
            Transfer Funds
          </Button>
        ) : (
          <Button
            onClick={handleArchiveConfirm}
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
          >
            {loading ? 'Archiving...' : 'Archive Wallet'}
          </Button>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Transfer Funds</h3>
        <p className="text-slate-600">
          Transfer {formatCurrency(wallet.balance)} to another wallet before archiving.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Destination Wallet *
          </label>
          <input
            type="text"
            placeholder="Search wallets by name or GHIN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <Card className="mt-2 max-h-48 overflow-y-auto">
            <CardContent className="p-2">
              {filteredWallets.length > 0 ? (
                <div className="space-y-1">
                  {filteredWallets.map(w => (
                    <div
                      key={w._id}
                      onClick={() => {
                        setTransferData(prev => ({ ...prev, toWalletId: w._id }));
                        setSearchQuery(w.ownerName);
                      }}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-md cursor-pointer transition-colors",
                        transferData.toWalletId === w._id
                          ? "bg-blue-50 border-2 border-blue-500"
                          : "hover:bg-slate-50 border-2 border-transparent"
                      )}
                    >
                      <span className="text-2xl">{getOwnerTypeIcon(w.ownerType)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{w.ownerName}</div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {w.ownerType === 'member' && w.ownerDetails?.ghinNumber && (
                            <span>GHIN: {w.ownerDetails.ghinNumber}</span>
                          )}
                          <span className="font-medium text-slate-700">{formatCurrency(w.balance)}</span>
                        </div>
                      </div>
                      {transferData.toWalletId === w._id && (
                        <Badge variant="success" className="shrink-0">Selected</Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-500 text-sm">
                  {searchQuery ? 'No wallets match your search' : 'No wallets available'}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Transfer Description
          </label>
          <textarea
            value={transferData.description}
            onChange={(e) => setTransferData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Description for the transfer..."
            rows="3"
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={() => setStep(1)}>
          ← Back
        </Button>
        <Button
          onClick={handleTransferSubmit}
          disabled={loading || !transferData.toWalletId}
          className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
        >
          {loading ? 'Transferring...' : `Transfer ${formatCurrency(wallet.balance)}`}
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Confirm Archive</h3>
        <p className="text-slate-600">
          Funds have been transferred successfully. Ready to archive wallet.
        </p>
      </div>

      <Alert variant="success" className="border-green-200 bg-green-50">
        <div className="flex gap-3 items-center">
          <span className="text-3xl">✅</span>
          <AlertDescription className="text-green-800">
            <strong>{formatCurrency(wallet.balance)}</strong> has been transferred to{' '}
            <strong>{availableWallets.find(w => w._id === transferData.toWalletId)?.ownerName}</strong>
          </AlertDescription>
        </div>
      </Alert>

      <Alert variant="warning" className="border-amber-200 bg-amber-50">
        <div className="flex gap-3">
          <span className="text-2xl">⚠️</span>
          <AlertDescription className="text-amber-800">
            <strong>This action cannot be undone.</strong> The wallet will be archived and cannot accept new transactions.
          </AlertDescription>
        </div>
      </Alert>

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button
          onClick={handleArchiveConfirm}
          disabled={loading}
          className="flex-1 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
        >
          {loading ? 'Archiving...' : 'Archive Wallet'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <Card className="relative z-10 w-full max-w-lg mx-4 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <CardHeader className="border-b border-slate-100 pb-4">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all",
                  step >= s
                    ? step > s
                      ? "bg-green-500 text-white"
                      : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white"
                    : "bg-slate-100 text-slate-400"
                )}>
                  {step > s ? '✓' : s}
                </div>
                {s < 3 && (
                  <div className={cn(
                    "w-12 h-1 rounded-full transition-all",
                    step > s ? "bg-green-500" : "bg-slate-200"
                  )} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Close Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute right-4 top-4 h-8 w-8 p-0 text-slate-500 hover:text-slate-900"
          >
            ×
          </Button>
        </CardHeader>

        <CardContent className="p-6">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <div className="flex gap-3">
                <span className="text-xl">⚠️</span>
                <AlertDescription>{error}</AlertDescription>
              </div>
            </Alert>
          )}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </CardContent>
      </Card>
    </div>
  );
}

export default ArchiveWalletModal;
